/**
 * DraftTicketCard — bespoke visual rendering of a ticket draft.
 *
 * Two visual variants:
 *   - 'full'    → used in the right pane of the LogFeatureModal. Large,
 *                 spacious, hero-style preview the operator can scan.
 *   - 'compact' → used inline in chat tool-call results. Same content,
 *                 tighter padding so it doesn't dominate the thread.
 *
 * The card is purely presentational — submission is driven by the agent
 * via the `submit_ticket` tool, not by a button on this card. The CTA is
 * the descriptive footer: "Say 'submit it' to create the ticket".
 */

import type { CSSProperties, ReactNode } from 'react'
import { FileText } from 'lucide-react'
import type { DraftTicketPayload } from '../types'
import {
  formatTicketType,
  ticketTypeAccent,
  severityAccent,
  formatSeverityLabel,
} from './ticket-visuals'

interface DraftTicketCardProps {
  data: DraftTicketPayload
  variant?: 'full' | 'compact'
}

export function DraftTicketCard({
  data,
  variant = 'full',
}: DraftTicketCardProps): ReactNode {
  const isFull = variant === 'full'
  const typeAccent = ticketTypeAccent(data.type)
  const sevAccent = severityAccent(data.severity)

  return (
    <div
      style={{
        ...styles.card,
        ...(isFull ? styles.cardFull : styles.cardCompact),
      }}
      data-testid={`draft-ticket-card-${variant}`}
    >
      {/* Pulse ring across top to telegraph "live, unsubmitted" */}
      <div style={styles.pulseRail} className="log-feature-pulse" />

      {/* Header: type + severity pills, draft tag */}
      <div style={isFull ? styles.headerFull : styles.headerCompact}>
        <div style={styles.pillRow}>
          <span
            style={{
              ...styles.pill,
              color: typeAccent.fg,
              background: typeAccent.bg,
              border: `1px solid ${typeAccent.border}`,
            }}
          >
            <span style={{ ...styles.pillDot, background: typeAccent.fg }} />
            {formatTicketType(data.type)}
          </span>
          <span
            style={{
              ...styles.pill,
              color: sevAccent.fg,
              background: sevAccent.bg,
              border: `1px solid ${sevAccent.border}`,
            }}
          >
            {formatSeverityLabel(data.severity)}
          </span>
        </div>
        <span style={styles.draftBadge}>
          <FileText size={11} />
          Draft
        </span>
      </div>

      {/* Title */}
      <h2
        style={isFull ? styles.titleFull : styles.titleCompact}
        data-testid="draft-ticket-title"
      >
        {data.title}
      </h2>

      {/* Description block */}
      <div
        style={{
          ...styles.descriptionBlock,
          ...(isFull ? styles.descriptionFull : styles.descriptionCompact),
        }}
      >
        <span style={styles.sectionLabel}>Description</span>
        <p
          style={{
            ...styles.descriptionText,
            ...(isFull ? styles.descriptionTextFull : {}),
          }}
        >
          {data.description}
        </p>
      </div>

      {/* Metadata grid */}
      <div style={styles.metaGrid}>
        <MetaItem label="Entity" value={data.entity_id} mono />
        {data.affected_area ? (
          <MetaItem label="Affected area" value={data.affected_area} mono />
        ) : null}
        <MetaItem
          label="Tags"
          value={
            data.tags && data.tags.length > 0 ? (
              <span style={styles.tagRow}>
                {data.tags.map((tag) => (
                  <span key={tag} style={styles.tagChip}>
                    {tag}
                  </span>
                ))}
              </span>
            ) : (
              <span style={styles.metaEmpty}>—</span>
            )
          }
        />
        <MetaItem
          label="Draft ID"
          value={
            <span style={styles.draftIdValue} title={data.draft_id}>
              {data.draft_id.slice(0, 8)}…
            </span>
          }
          mono
        />
      </div>

      {/* Footer instruction */}
      <div style={styles.footer}>
        <span style={styles.footerHint}>
          Review the draft above. Tell the agent <em style={styles.em}>"submit it"</em> to
          create the ticket, or describe any changes you want.
        </span>
      </div>
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function MetaItem({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div style={styles.metaItem}>
      <span style={styles.metaLabel}>{label}</span>
      <span
        style={{
          ...styles.metaValue,
          ...(mono ? styles.metaValueMono : {}),
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
  },
  cardFull: {
    gap: '20px',
    padding: '24px',
  },
  cardCompact: {
    gap: '12px',
    padding: '14px',
  },
  pulseRail: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background:
      'linear-gradient(90deg, transparent 0%, var(--accent-primary) 50%, transparent 100%)',
    opacity: 0.6,
  },
  headerFull: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  headerCompact: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  pillDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  draftBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '10.5px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  titleFull: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  titleCompact: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--text-primary)',
  },
  descriptionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  descriptionFull: {
    padding: '14px 16px',
  },
  descriptionCompact: {
    padding: '10px 12px',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  descriptionText: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.55,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  descriptionTextFull: {
    fontSize: '14px',
    lineHeight: 1.65,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px 20px',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  metaLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  metaValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaValueMono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  metaEmpty: {
    color: 'var(--text-muted)',
  },
  tagRow: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '6px',
    whiteSpace: 'normal',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    fontSize: '11px',
    color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '999px',
  },
  draftIdValue: {
    fontFamily: "'JetBrains Mono', monospace",
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingTop: '12px',
    borderTop: '1px dashed var(--border-subtle)',
  },
  footerHint: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.5,
  },
  em: {
    color: 'var(--accent-primary)',
    fontStyle: 'normal',
    fontWeight: 600,
  },
}

export default DraftTicketCard
